<template>
  <div
    class="wrapper calm-layout"
    :class="{ 'calm-rail': railCollapsed, 'calm-drawer-open': sidebarShown }"
  >
    <!-- ═══════════ SIDEBAR (Calm 3-level legacy-faithful shell) ═══════════ -->
    <aside class="calm-aside calm-shell">
      <div class="calm-aside__head">
        <router-link to="/lmg/v2/transaction" class="calm-aside__brand">
          <span class="calm-aside__brand-mark">North And East Broker</span>
          <span class="calm-aside__brand-txt">Backoffice</span>
        </router-link>
        <button
          type="button"
          class="calm-aside__collapse"
          :aria-label="$t('nav.toggleRail')"
          @click="toggleRail"
        >
          <calm-icon name="panel-left" :size="18" />
        </button>
      </div>

      <calm-sidebar :collapsed="railCollapsed" @navigate="onNavigate" />

      <a class="calm-aside__logout" @click="handleLogout">
        <calm-icon name="panel-left" :size="16" class="calm-aside__logout-ic" />
        <span class="calm-aside__logout-txt">{{ menu_logout }}</span>
      </a>
    </aside>

    <!-- mobile hamburger (only <1200px; opens the drawer) -->
    <button
      type="button"
      class="calm-burger"
      :aria-label="$t('nav.primary')"
      @click="$sidebar.displaySidebar(true)"
    >
      <calm-icon name="menu" :size="22" />
    </button>

    <!-- mobile drawer scrim -->
    <div class="calm-scrim" @click="$sidebar.displaySidebar(false)"></div>

    <!-- ═══════════ CONTENT ═══════════ -->
    <div class="main-content">
      <dashboard-navbar :type="$route.meta.navbarType"></dashboard-navbar>

      <div @click="$sidebar.displaySidebar(false)">
        <fade-transition :duration="200" origin="center top" mode="out-in">
          <router-view></router-view>
        </fade-transition>
      </div>
      <content-footer v-if="!$route.meta.hideFooter"></content-footer>
    </div>
  </div>
</template>
<script>
/* eslint-disable no-new */
import DashboardNavbar from "./DashboardNavbar.vue";
import ContentFooter from "./ContentFooter.vue";
import DashboardContent from "./Content.vue";
import { FadeTransition } from "vue2-transitions";
import { mapGetters, mapActions } from "vuex";
import CalmSidebar from "@/components/lmg/CalmSidebar.vue";
import CalmIcon from "@/components/lmg/CalmIcon.vue";

const LS_RAIL = "nav.collapsed.rail";

export default {
  components: {
    DashboardNavbar,
    ContentFooter,
    DashboardContent,
    FadeTransition,
    CalmSidebar,
    CalmIcon,
  },
  data() {
    let rail = false;
    try {
      rail = localStorage.getItem(LS_RAIL) === "true";
    } catch (e) {
      rail = false;
    }
    return { railCollapsed: rail };
  },
  computed: {
    menu_logout: function () {
      return this.$t("auth.signout");
    },
    // reactive: $sidebar is a Vue-observed store (SidebarPlugin)
    sidebarShown: function () {
      return this.$sidebar ? this.$sidebar.showSidebar : false;
    },
    ...mapGetters({
      getUser: "Auth/getUser",
    }),
  },
  methods: {
    ...mapActions({
      setUser: "Auth/setUser",
      signOut: "Auth/signOut",
    }),
    toggleRail() {
      this.railCollapsed = !this.railCollapsed;
      try {
        localStorage.setItem(LS_RAIL, this.railCollapsed ? "true" : "false");
      } catch (e) {
        /* ignore */
      }
    },
    // close the mobile drawer after navigating to a page
    onNavigate() {
      if (this.$sidebar) this.$sidebar.displaySidebar(false);
    },
    handleLogout() {
      this.$cookie.delete("token");
      this.signOut();
      this.$router.push("/login");
    },
  },
};
</script>
<style lang="scss">
/* ── Calm shell layout (scoped under .calm-layout so other layouts are untouched) ──
   All nav component classes are namespaced `calm-*` to avoid Bootstrap collisions
   (.row/.card/.badge). Design tokens mirror doc/legacy + the project's calm.scss. */
.calm-layout {
  --c-bg: #f5f7fa;
  --c-surface: #ffffff;
  --c-surface-soft: #fafbfd;
  --c-tint: #eef3fa;
  --c-text: #2c3e50;
  --c-text-soft: #5e6c80;
  --c-text-mute: #8a96a8;
  --c-border: #e4e9f1;
  --c-border-strong: #c7d1de;
  --c-blue: #3b6ea8;
  --c-blue-deep: #2c5282;
  --c-blue-soft: #ebf2fa;
  --c-font: "IBM Plex Sans Thai", "IBM Plex Sans", -apple-system, system-ui, sans-serif;
  --c-aside: 248px;
  --c-rail: 60px;

  background: var(--c-bg);
  min-height: 100vh;
}

/* aside */
.calm-layout .calm-aside {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: var(--c-aside);
  background: var(--c-surface);
  border-right: 1px solid var(--c-border);
  display: flex;
  flex-direction: column;
  z-index: 1030;
  overflow-y: auto;
  overflow-x: hidden;
  transition: width 220ms ease, transform 220ms ease;
}
.calm-layout.calm-rail .calm-aside { width: var(--c-rail); }

.calm-layout .calm-aside__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 56px;
  padding: 0 14px;
  border-bottom: 1px solid var(--c-border);
  flex: none;
}
.calm-layout .calm-aside__brand {
  display: flex;
  align-items: baseline;
  gap: 7px;
  text-decoration: none;
  min-width: 0;
  flex: 1 1 auto;
}
.calm-layout .calm-aside__brand-mark {
  font-weight: 700;
  font-size: 13px;
  color: var(--c-blue-deep);
  letter-spacing: 0.3px;
  /* Cap width and ellipsis so a longer brand can't push the sidebar wider. */
  max-width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.calm-layout .calm-aside__brand-txt {
  font-size: 12px;
  color: var(--c-text-mute);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.calm-layout.calm-rail .calm-aside__brand-txt { display: none; }
/* Rail (60px) can't fit a multi-word brand — hide the mark too. */
.calm-layout.calm-rail .calm-aside__brand-mark { display: none; }
.calm-layout .calm-aside__collapse {
  flex: none;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border: 0;
  background: transparent;
  border-radius: 8px;
  color: var(--c-text-soft);
  cursor: pointer;
}
.calm-layout .calm-aside__collapse:hover { background: var(--c-bg); }

.calm-layout .calm-aside > .calm-nav { flex: 1; }

.calm-layout .calm-aside__logout {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 8px 10px 14px;
  padding: 9px 12px;
  border-radius: 8px;
  color: var(--c-text-soft);
  font-size: 13px;
  cursor: pointer;
  flex: none;
}
.calm-layout .calm-aside__logout:hover { background: var(--c-bg); color: var(--c-blue-deep); }
.calm-layout.calm-rail .calm-aside__logout-txt { display: none; }
.calm-layout.calm-rail .calm-aside__logout { justify-content: center; }

/* content offset */
.calm-layout .main-content {
  margin-left: var(--c-aside);
  transition: margin-left 220ms ease;
  min-height: 100vh;
  background: var(--c-bg);
}
.calm-layout.calm-rail .main-content { margin-left: var(--c-rail); }

/* scrim + burger hidden on desktop */
.calm-layout .calm-scrim { display: none; }
.calm-layout .calm-burger { display: none; }

/* ── mobile / iPad portrait (<1200, matches $sidebar breakpoint) ── */
@media (max-width: 1199.98px) {
  .calm-layout .calm-burger {
    display: grid;
    place-items: center;
    position: fixed;
    top: 10px;
    left: 12px;
    z-index: 1040;
    width: 40px;
    height: 40px;
    border: 1px solid var(--c-border);
    background: var(--c-surface);
    border-radius: 8px;
    color: var(--c-text-soft);
    box-shadow: 0 2px 8px rgba(44, 62, 80, 0.12);
    cursor: pointer;
  }
  .calm-layout.calm-drawer-open .calm-burger { display: none; }
  .calm-layout .calm-aside {
    transform: translateX(-100%);
    width: var(--c-aside);
    box-shadow: 0 8px 28px rgba(44, 62, 80, 0.2);
  }
  .calm-layout.calm-rail .calm-aside { width: var(--c-aside); } /* drawer is always full width */
  .calm-layout.calm-drawer-open .calm-aside { transform: translateX(0); }
  .calm-layout .main-content { margin-left: 0; }
  .calm-layout.calm-rail .main-content { margin-left: 0; }
  .calm-layout.calm-drawer-open .calm-scrim {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(44, 62, 80, 0.4);
    z-index: 1029;
  }
  /* 44px touch rows in the drawer */
  .calm-layout .calm-nav-row,
  .calm-layout .calm-nav-leaf { min-height: 44px; }
  /* a drawer is never an icon-rail */
  .calm-layout.calm-rail .calm-nav--collapsed { padding: 10px; align-items: stretch; }
  .calm-layout.calm-rail .calm-nav--collapsed .calm-nav-row,
  .calm-layout.calm-rail .calm-nav--collapsed .calm-nav-leaf {
    width: auto;
    justify-content: flex-start;
    padding-left: 30px;
  }
  .calm-layout.calm-rail .calm-nav--collapsed .calm-nav-row { padding-left: 10px; }
  .calm-layout.calm-rail .calm-nav--collapsed .calm-nav-row__label,
  .calm-layout.calm-rail .calm-nav--collapsed .calm-nav-row__caret,
  .calm-layout.calm-rail .calm-nav--collapsed .calm-nav-leaf__label,
  .calm-layout.calm-rail .calm-nav--collapsed .calm-nav-leaf__bullet { display: inline-flex; }
  .calm-layout.calm-rail .calm-nav--collapsed .calm-nav-divider { display: flex; }
  .calm-layout.calm-rail .calm-nav--collapsed .calm-nav-children { display: block; }
}

.text-app {
  color: #19aaf8;
}
</style>
